import type { TileArea } from "@/cache/SpatioTemporalTileCache/TileArea";
import type { TileGeometry } from "@/cache/SpatioTemporalTileCache/TileGeometry";
import type { MapViewport } from "@/layers/MapViewport";

/**
 * Checks if a tile is fully inside the tile area.
 */
export function tileInsideArea(tile: TileGeometry, area: TileArea): boolean {
  return (
    tile.z === area.z &&
    tile.x >= area.xyMin[0] &&
    tile.x <= area.xyMax[0] &&
    tile.y >= area.xyMin[1] &&
    tile.y <= area.xyMax[1]
  );
}

/**
 * Checks if a tile is near (touching) the tile area, e.g., in a 1-tile padding around it.
 */
export function tileTouchesArea(tile: TileGeometry, area: TileArea): boolean {
  if (tile.z !== area.z) {
    return false;
  }

  const pad = 3;

  const minX = area.xyMin[0] - pad;
  const maxX = area.xyMax[0] + pad;
  const minY = area.xyMin[1] - pad;
  const maxY = area.xyMax[1] + pad;

  return tile.x >= minX && tile.x <= maxX && tile.y >= minY && tile.y <= maxY;
}

export function calculatePriority(tile: TileGeometry, viewport: TileArea): number {
  let score = tile.z * 10;

  if (tileInsideArea(tile, viewport)) {
    score += 100; // Highest priority for visible
  } else if (tileTouchesArea(tile, viewport)) {
    score += 20; // Nearby padding tiles
  } else {
    score += 5; // Background preload
  }

  return score;
}

export function isVirtualTileOutsideViewport(virtualX: number, virtualY: number, viewport: MapViewport): boolean {
  return (
    virtualX < viewport.viewportArea.xyMin[0] ||
    virtualX > viewport.viewportArea.xyMax[0] ||
    virtualY < viewport.viewportArea.xyMin[1] ||
    virtualY > viewport.viewportArea.xyMax[1]
  );
}

export function calculateTextureCoordinates(dx: number, dy: number, tilesPerSide: number) {
  return {
    texLeft: dx / tilesPerSide,
    texRight: (dx + 1) / tilesPerSide,
    texTop: dy / tilesPerSide,
    texBottom: (dy + 1) / tilesPerSide,
  };
}
